import React, { useRef, useEffect, useState } from "react";
import {
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  Maximize2,
  Bold,
  Italic,
  Underline,
  Strikethrough,
  List,
  ListOrdered,
  Type,
  Subscript
} from "lucide-react";

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  rows?: number; // Kept for compatibility, though min-height is better
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  placeholder = "Enter text...",
  className = "",
}) => {
  const contentRef = useRef<HTMLDivElement>(null);
  const [showFontSizeDropdown, setShowFontSizeDropdown] = useState(false);
  const [fontSize, setFontSize] = useState("3"); // 1-7 scale for execCommand


  // Sync external value to internal contentEditable only if different to avoid cursor jumps
  useEffect(() => {
    if (contentRef.current && contentRef.current.innerHTML !== value) {
      // Only update if the value is meaningfully different to prevent loop
      // Simple check: if value is empty, clear it. If not, set it.
      // But for typing updates, we rely on onInput.
      // This effect is mainly for initial load or external updates.
      if (document.activeElement !== contentRef.current) {
        contentRef.current.innerHTML = value;
      }
    }
  }, [value]);

  const handleFormat = (command: string, value?: string) => {
    document.execCommand(command, false, value);

    // For lists, ensure bullets/numbers are visible even if global CSS resets them
    if (command === "insertUnorderedList" || command === "insertOrderedList") {
      try {
        const selection = window.getSelection();
        if (selection && selection.anchorNode) {
          let node: HTMLElement | null =
            (selection.anchorNode as HTMLElement).nodeType === 1
              ? (selection.anchorNode as HTMLElement)
              : (selection.anchorNode.parentElement as HTMLElement | null);

          while (node && node !== contentRef.current) {
            if (node.tagName === "UL" || node.tagName === "OL") {
              if (node.tagName === "UL") {
                node.style.listStyleType = "disc";
              } else {
                node.style.listStyleType = "decimal";
              }
              node.style.paddingLeft = "1.25rem";
              break;
            }
            node = node.parentElement;
          }
        }
      } catch {
        // fail silently – formatting will still apply even if this styling tweak fails
      }
    }

    contentRef.current?.focus();
    updateValue();
  };

  const updateValue = () => {
    if (contentRef.current) {
      onChange(contentRef.current.innerHTML);
    }
  };

  const fontSizes = [
    { label: "Small", value: "2" },
    { label: "Normal", value: "3" },
    { label: "Large", value: "5" },
    { label: "Huge", value: "7" },
  ];

  return (
    <div className={`border border-gray-300 rounded-lg overflow-hidden flex flex-col ${className}`}>
      {/* Toolbar */}
      <div className="bg-[#885ABB] border-b border-gray-300 p-1 flex gap-1 items-center flex-wrap">
        <button
          className="p-1.5 hover:bg-purple-700 rounded text-white transition"
          onClick={() => handleFormat("bold")}
          title="Bold"
        >
          <Bold size={14} />
        </button>
        <button
          className="p-1.5 hover:bg-purple-700 rounded text-white transition"
          onClick={() => handleFormat("italic")}
          title="Italic"
        >
          <Italic size={14} />
        </button>
        <button
          className="p-1.5 hover:bg-purple-700 rounded text-white transition"
          onClick={() => handleFormat("underline")}
          title="Underline"
        >
          <Underline size={14} />
        </button>
        <button
          className="p-1.5 hover:bg-purple-700 rounded text-white transition"
          onClick={() => handleFormat("strikeThrough")}
          title="Strikethrough"
        >
          <Strikethrough size={14} />
        </button>

        <div className="w-px bg-white/30 h-4 mx-1"></div>

        <button
          className="p-1.5 hover:bg-purple-700 rounded text-white transition"
          onClick={() => handleFormat("insertUnorderedList")}
          title="Bullet List"
        >
          <List size={14} />
        </button>
        <button
          className="p-1.5 hover:bg-purple-700 rounded text-white transition"
          onClick={() => handleFormat("insertOrderedList")}
          title="Numbered List"
        >
          <ListOrdered size={14} />
        </button>

        <div className="w-px bg-white/30 h-4 mx-1"></div>

        <button
          className="p-1.5 hover:bg-purple-700 rounded text-white transition"
          onClick={() => handleFormat("justifyLeft")}
          title="Left Align"
        >
          <AlignLeft size={14} />
        </button>
        <button
          className="p-1.5 hover:bg-purple-700 rounded text-white transition"
          onClick={() => handleFormat("justifyCenter")}
          title="Center Align"
        >
          <AlignCenter size={14} />
        </button>
        <button
          className="p-1.5 hover:bg-purple-700 rounded text-white transition"
          onClick={() => handleFormat("justifyRight")}
          title="Right Align"
        >
          <AlignRight size={14} />
        </button>

        <div className="w-px bg-white/30 h-4 mx-1"></div>

        {/* Font Size */}
        <div className="relative">
          <button
            onClick={() => setShowFontSizeDropdown(!showFontSizeDropdown)}
            className="flex items-center gap-1 px-2 py-1 text-xs text-white bg-purple-600/50 rounded hover:bg-purple-600"
          >
            <Type size={12} />
            Size
          </button>
          {showFontSizeDropdown && (
            <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded shadow-lg z-50 py-1 min-w-[80px]">
              {fontSizes.map((size) => (
                <button
                  key={size.value}
                  onClick={() => {
                    handleFormat("fontSize", size.value);
                    setShowFontSizeDropdown(false);
                  }}
                  className="w-full text-left px-3 py-1.5 text-xs hover:bg-gray-100 text-gray-700"
                >
                  {size.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Editable Content */}
      <div
        ref={contentRef}
        contentEditable
        onInput={updateValue}
        onBlur={updateValue} // Ensure final sync
        className="flex-1 bg-gray-50 p-3 outline-none overflow-y-auto text-sm min-h-[120px] focus:bg-white transition-colors"
        style={{ whiteSpace: "pre-wrap" }}
      // Note: pre-wrap helps presere spacing but HTML handles newlines as <br> or <div> usually
      />
    </div>
  );
};

export default RichTextEditor;
